import React, { useMemo } from 'react';
import { Button, Select, TextField, Checkbox, Label } from '@doxyme/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextArea } from '../../../../components/form';
import { useForm, Controller } from 'react-hook-form';
import { FormRow } from './index';
import { AvailableForListOptions, StatusListOptions, TariffListOptions, templateExtension, } from '../../constants';
import { useValidation } from '../../../core/hooks';
import { nullOrNumber } from '../../helpers';
import { TYPES } from '../../constants/constants';
import './extension-form.scss';
export const ExtensionForm = ({ extension = templateExtension, onSave, disableSubmitBtn = false, }) => {
    const { handleSubmit, register, watch, setValue, formState, control } = useForm({
        mode: 'onTouched',
        shouldUnregister: true,
        defaultValues: extension,
    });
    const watchIsFake = watch('isFake', extension.isFake);
    const isExternalType = watch('type') === TYPES.EXTERNAL_TOOL;
    const hideAvailableForSection = [TYPES.EXTERNAL_TOOL, TYPES.INFO].includes(watch('type'));
    const validationHelpers = useValidation();
    const { formatMessage } = useIntl();
    const onSubmit = async (data) => {
        onSave({
            ...extension,
            ...data,
            price: Number.isNaN(data.price) ? null : data.price,
            supportArticleLink: data.supportArticleLink || null,
        });
    };
    const ctaButtonOptions = useMemo(() => [
        {
            label: formatMessage({ id: 'account.marketplace.action-install' }),
            value: TYPES.EXTENSION,
        },
        {
            label: formatMessage({
                id: 'account.marketplace.action-external-tool',
            }),
            value: TYPES.EXTERNAL_TOOL,
        },
        {
            label: formatMessage({ id: 'account.marketplace.action-no-button' }),
            value: TYPES.INFO,
        },
    ], []);
    return (<form onSubmit={handleSubmit(onSubmit)}>
      <TextField name="title" label={<FormattedMessage id="account.administration.extentions.title"/>} formGroupClassName="extention-form-field" ref={register({
            required: formatMessage({ id: 'form.errors.field-required' }, { field: 'Title' }),
        })} hasError={Boolean(formState.errors.title)} errorText={formState.errors.title && formState.errors.title.message}/>
      <TextField name="name" label={<FormattedMessage id="account.administration.extentions.name"/>} formGroupClassName="extention-form-field" placeholder={formatMessage({
            id: 'account.administration.extentions.name-placeholder',
        })} ref={register({
            required: formatMessage({ id: 'form.errors.field-required' }, { field: 'Name' }),
        })} hasError={Boolean(formState.errors.name)} errorText={formState.errors.name && formState.errors.name.message}/>
      <TextField name="shortDescription" label={<FormattedMessage id="account.administration.extentions.short-description"/>} formGroupClassName="extention-form-field" ref={register}/>
      <TextField name="icon" label={<FormattedMessage id="account.administration.extentions.icon-url"/>} formGroupClassName="extention-form-field" ref={register}/>
      <Select name="status" id="status" formGroupClassName="extention-form-field" label={<FormattedMessage id="account.administration.extentions.status"/>} control={control} options={StatusListOptions}/>
      <FormRow label={<FormattedMessage id="account.administration.extentions.opt-description"/>}>
        <TextArea name="optionalDescription" rows={2} ref={register}/>
      </FormRow>

      <hr />
      <div className="ui-form-group extention-form-field">
        <Label id="isFake" label="Fakedoor"/>
        <div>
          <Controller control={control} name="isFake" render={({ onBlur, onChange, value }) => {
            return (<Checkbox id="isFake" onBlur={onBlur} onChange={(e) => onChange(e.target.checked)} checked={value}/>);
        }}/>
        </div>
      </div>
      {!watchIsFake && (<div data-testid="fake-fields">
          <Select name="type" id="cta-button" formGroupClassName="extention-form-field" label={<FormattedMessage id="account.administration.extentions.cta-button"/>} rules={{
                required: formatMessage({ id: 'form.errors.field-required' }, { field: 'CTA Button' }),
            }} errors={formState.errors} control={control} options={ctaButtonOptions}/>
          {isExternalType && (<TextField name="buttonValue" id="cta-button-select" label={<FormattedMessage id="account.administration.extentions.cta-button-url"/>} formGroupClassName="extention-form-field" ref={register({
                    required: formatMessage({ id: 'form.errors.required' }),
                    validate: validationHelpers.url,
                })} hasError={Boolean(formState.errors.buttonValue)} errorText={formState.errors.buttonValue &&
                    formState.errors.buttonValue.message}/>)}
          <FormRow label={<FormattedMessage id="account.administration.extentions.description"/>}>
            <TextArea name="description" rows={8} ref={register}/>
          </FormRow>

          <TextField name="hint" label={<FormattedMessage id="account.administration.extentions.hint"/>} formGroupClassName="extention-form-field" ref={register}/>
          <TextField name="video" label={<FormattedMessage id="account.administration.extentions.media-url"/>} formGroupClassName="extention-form-field" ref={register}/>
          <TextField name="source" label={<FormattedMessage id="account.administration.extentions.source-company"/>} formGroupClassName="extention-form-field" ref={register}/>
          <TextField name="termsUrl" label={<FormattedMessage id="account.administration.extentions.terms-url"/>} formGroupClassName="extention-form-field" ref={register}/>
          <TextField name="email" label={<FormattedMessage id="account.administration.extentions.email"/>} type="email" formGroupClassName="extention-form-field" ref={register}/>
          <TextField name="supportArticleLink" label={<FormattedMessage id="account.administration.extentions.support-link"/>} formGroupClassName="extention-form-field" hasError={Boolean(formState.errors.supportArticleLink)} errorText={formState.errors.supportArticleLink &&
                formState.errors.supportArticleLink.message} ref={register({
                validate: validationHelpers.url,
            })}/>
          <TextField name="supportArticleText" label={<FormattedMessage id="account.administration.extentions.support-link-text"/>} formGroupClassName="extention-form-field" ref={register}/>
          {!hideAvailableForSection && (<div data-testid="button-types-fields">
              <TextField name="sourceCode" label={<FormattedMessage id="account.administration.extentions.source-code"/>} formGroupClassName="extention-form-field" ref={register}/>
              <hr />
              <Select name="availableFor" id="availableFor" formGroupClassName="extention-form-field" label={<FormattedMessage id="account.administration.extentions.availableFor"/>} control={control} options={AvailableForListOptions} errors={formState.errors} rules={{
                    required: (<FormattedMessage id="form.errors.field-required" values={{ field: 'Available for' }}/>),
                }}/>
              <Select name="tariff" id="tariff" formGroupClassName="extention-form-field" label={<FormattedMessage id="account.administration.extentions.tariff"/>} control={control} options={TariffListOptions} errors={formState.errors} rules={{
                    required: (<FormattedMessage id="form.errors.field-required" values={{ field: 'Tariff' }}/>),
                }}/>
              <TextField name="price" label={<FormattedMessage id="account.administration.extentions.price"/>} type="number" step="any" formGroupClassName="extention-form-field" defaultValue={extension.price} onChange={(e) => {
                    setValue('price', nullOrNumber(e.target.value));
                }} ref={register({ valueAsNumber: true })}/>
            </div>)}
        </div>)}
      <hr />
      <FormRow>
        <Button btnStyle="solid" size="medium" type="submit" disabled={disableSubmitBtn}>
          <FormattedMessage id="account.administration.extentions.save"/>
        </Button>
      </FormRow>
    </form>);
};
